import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { sitesSerivces } from "../../services";
import { showToast, Loading, CustomDialog } from "../../components";
import { Parcelle } from "../../interfaces/site";
import { MultiSelect } from "primereact/multiselect";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../utils/UserProvider";

interface ModalAddSiteProps {
  modalIsOpenSite: boolean;
  closeModalSite: () => void;
  parcelles: Parcelle[];
}

const ModalAddSite = ({
  modalIsOpenSite,
  closeModalSite,
  parcelles,
}: ModalAddSiteProps) => {
  const queryClient = useQueryClient();
  const { client } = useAuth();
  const [loading, setLoading] = useState<boolean>(false);
  const [options, setOptions] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState<Parcelle[]>([]);
  const [parcelle_ids, setParcelleIds] = useState<number[]>([]);
  const [parcelleError, setParcelleError] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    const mappedOptions = parcelles?.map((parcelle) => ({
      value: parcelle.id,
      label: parcelle.numéro,
    }));
    setOptions(mappedOptions);
  }, [parcelles]);

  const onSubmit = async (data: any) => {
    if (parcelle_ids.length === 0) {
      setParcelleError(true);
      return;
    }

    try {
      const siteData = {
        nom_site: data.nom_site,
        env_client_id: client.id,
        parcelles: parcelle_ids,
      };
      setLoading(true);
      await sitesSerivces.post(siteData).then(() => {
        setLoading(false);
        showToast("success", "Site ajouté avec succès");
        queryClient.refetchQueries({
          queryKey: ["sites"],
        });
        reset();
        setParcelleIds([]);
        setSelectedOptions([]);
        setParcelleError(false);
        closeModalSite();
      });
    } catch (e) {
      showToast("info", "Site avec ce nom déja ajouté");
      setLoading(false);
      reset();
      setSelectedOptions([]);
      setParcelleIds([]);
    }
  };

  const handleSelectChange = (e) => {
    setSelectedOptions(e.value);
    setParcelleError(false);
    if (e.value.length !== 0) {
      setLoading(false);
      setParcelleIds(e.value);
    } else {
      setParcelleIds([]);
    }
  };

  return (
    <>
      <CustomDialog
        header="Ajouter un nouveau site"
        visible={modalIsOpenSite}
        closeModalDialog={() => {
          closeModalSite();
          setSelectedOptions([]);
          setParcelleIds([]);
          setParcelleError(false);
          reset();
        }}
      >
        <div className="w-full h-full relative">
          <div className="p-6 space-y-6">
            <form
              onSubmit={handleSubmit(onSubmit)}
              encType="multipart/form-data"
            >
              <div className="grid grid-cols-6 gap-6">
                <div className="col-span-6 sm:col-span-6">
                  <label
                    htmlFor="nom_site"
                    className="block text-md font-bold leading-6 text-gray-900"
                  >
                    Nom *
                  </label>
                  <input
                    type="text"
                    name="nom_site"
                    id="nom_site"
                    placeholder="Saisir votre nom de site"
                    className="my-3 border border-gray-300 text-gray-900 font-normal text-md rounded-lg focus:ring-gexpertise-100 focus:border-gexpertise-200 block w-full p-2.5"
                    {...register("nom_site", {
                      required: true,
                    })}
                  />
                  {errors.nom_site && (
                    <span className="bg-red-100 text-red-800 text-md font-medium mr-2 mt-2 px-2.5 py-0.5 rounded">
                      champ obligatoire
                    </span>
                  )}
                </div>

                <div className="col-span-6 sm:col-span-6">
                  <label
                    htmlFor="parcelle"
                    className="block text-md font-bold leading-6 text-gray-900"
                  >
                    Choisir parcelle(s) *
                  </label>
                  <MultiSelect
                    className="my-3 border border-gray-300 text-gray-900 font-bold text-xl rounded-lg focus:ring-gexpertise-100 focus:border-gexpertise-200 w-full"
                    placeholder="Sélectionner vos parcelles"
                    options={options}
                    value={selectedOptions}
                    onChange={handleSelectChange}
                    name="numéro"
                    display="chip"
                    maxSelectedLabels={5}
                    filter
                  />
                  {parcelleError && (
                    <span className="bg-red-100 text-red-800 text-md font-medium mr-2 mt-2 px-2.5 py-0.5 rounded">
                      champ obligatoire
                    </span>
                  )}
                </div>
              </div>

              <p className="text-lg font-thin my-4">* obligatoire</p>

              <div className="flex justify-end">
                <button
                  type="submit"
                  className="text-white mt-5 bg-gexpertise-200 hover:bg-gexpertise-100 font-medium rounded-lg text-md px-6 py-3 text-center"
                >
                  {loading && (
                    <svg
                      aria-hidden="true"
                      role="status"
                      className="inline w-4 h-4 mr-3 text-white animate-spin"
                      viewBox="0 0 100 101"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                        fill="#E5E7EB"
                      />
                      <path
                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                        fill="currentColor"
                      />
                    </svg>
                  )}
                  Ajouter
                </button>
              </div>
            </form>
          </div>
        </div>
      </CustomDialog>
      {loading && <Loading />}
    </>
  );
};

export default ModalAddSite;